import React, { useState, ChangeEvent, KeyboardEvent } from "react";
import Select from "react-select";
import {  ParameterValue } from "./ParametersData";
import { formatString } from '../../utils/capitalized';

interface ParametersFormProps {
  selectedActivity: Record<string, ParameterValue>;
  parameters: Record<string, ParameterValue>;
  onChange: (parameters: Record<string, ParameterValue>) => void;
  onActivityChange?: (activity: string) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const formatParameterValue = (value: ParameterValue): string => {
  if (Array.isArray(value)) {
    if (value.length > 0 && Array.isArray(value[0])) {
      return 'Coordinates Array';
    }
    return value.join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

// Check if the value is a specific object type (Capacity, SpeedLimit, Distance)
const isObjectParameter = (key: string, value: ParameterValue): boolean => {
  return (
    typeof value === 'object' && 
    !Array.isArray(value) && 
    value !== null &&
    ['capacity', 'speed_limit', 'distance'].includes(key)
  );
};

export default function ParametersForm({ selectedActivity, parameters, onChange, onActivityChange }: ParametersFormProps) {
  const [newValue, setNewValue] = useState("");
  const [allOptions, setAllOptions] = useState<Record<string, Set<string>>>({});
  const [newObjectKey, setNewObjectKey] = useState("");
  const [newObjectValue, setNewObjectValue] = useState("");
  const [editingKeys, setEditingKeys] = useState<Record<string, string>>({});

  const handleParameterChange = (key: string, value: ParameterValue) => {
    if (isStringArray(value)) {
      // When options are deselected, ensure they remain in allOptions
      const currentValues = value as string[];
      const previousValues = (parameters[key] as string[]) || [];
      const removedValues = previousValues.filter(v => !currentValues.includes(v));
      
      if (removedValues.length > 0) {
        setAllOptions(prev => ({
          ...prev,
          [key]: new Set(Array.from(prev[key] || []).concat(removedValues))
        }));
      }
    }
    onChange({ ...parameters, [key]: value });
  };

  const handleAddNewValue = (key: string, newValueToAdd: string) => {
    if (newValueToAdd.trim() && isStringArray(parameters[key])) {
      const currentValues = parameters[key] as string[];
      const updatedValues = [...currentValues, newValueToAdd.trim()];
      
      // Add to all options first
      setAllOptions(prev => ({
        ...prev,
        [key]: new Set(Array.from(prev[key] || []).concat([newValueToAdd.trim()]))
      }));
      
      // Then update selected values
      handleParameterChange(key, updatedValues);
      setNewValue("");
    }
  };

  // Handle changes to object values (Capacity, SpeedLimit, Distance)
  const handleObjectValueChange = (key: string, objKey: string, objValue: number) => {
    const currentObj = { ...(parameters[key] as Record<string, number> || {}) };
    currentObj[objKey] = objValue;
    handleParameterChange(key, currentObj);
  };

  // Add new key-value pair to object parameters
  const handleAddObjectKeyValue = (key: string) => {
    if (newObjectKey.trim() && !isNaN(Number(newObjectValue))) {
      const currentObj = { ...(parameters[key] as Record<string, number> || {}) };
      currentObj[newObjectKey.trim()] = Number(newObjectValue);
      handleParameterChange(key, currentObj);
      setNewObjectKey("");
      setNewObjectValue("");
    }
  };

  // Delete key from object parameters
  const handleDeleteObjectKey = (key: string, objKey: string) => {
    const currentObj = { ...(parameters[key] as Record<string, number> || {}) };
    delete currentObj[objKey];
    handleParameterChange(key, currentObj);
  };

  const getAvailableOptions = (key: string, selectedValues: string[]): SelectOption[] => {
    return Array.from(allOptions[key] || [])
      .sort()
      .map(option => ({
        value: option,
        label: option
      }));
  };

  const isStringArray = (value: ParameterValue): value is string[] => {
    return Array.isArray(value) && (!value.length || typeof value[0] === 'string');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNewValue(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, key: string) => {
    if (e.key === 'Enter') {
      handleAddNewValue(key, newValue);
    }
  };

  // Handle the completion of editing a key
  const handleKeyEditComplete = (paramKey: string, oldKey: string, newKey: string, value: Record<string, number>) => {
    if (newKey.trim() === '' || (newKey !== oldKey && value[newKey] !== undefined)) {
      // Reset to original key if invalid
      setEditingKeys(prev => ({ ...prev, [oldKey]: oldKey }));
      return;
    }

    if (newKey !== oldKey) {
      // Create a new object with the updated key
      const currentObj = { ...(parameters[paramKey] as Record<string, number> || {}) };
      // Copy the value to the new key and delete the old one
      currentObj[newKey] = currentObj[oldKey];
      delete currentObj[oldKey];
      handleParameterChange(paramKey, currentObj);
    }

    // Clear the editing state for this key
    setEditingKeys(prev => {
      const newState = { ...prev };
      delete newState[oldKey];
      return newState;
    });
  };

  // Render object parameters (Capacity, SpeedLimit, Distance)
  const renderObjectParameter = (key: string, value: Record<string, number>) => {
    return (
      <div className="space-y-3">
        {Object.entries(value).map(([objKey, objValue]) => (
          <div key={objKey} className="flex gap-2 items-center">
            <input
              type="text"
              className="flex-1 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:border-blue-300"
              value={editingKeys[objKey] !== undefined ? editingKeys[objKey] : objKey}
              onChange={(e) => {
                setEditingKeys(prev => ({ ...prev, [objKey]: e.target.value }));
              }}
              onBlur={(e) => handleKeyEditComplete(key, objKey, e.currentTarget.value, value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleKeyEditComplete(key, objKey, e.currentTarget.value, value);
                }
                // Reset on Escape
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setEditingKeys(prev => {
                    const newState = { ...prev };
                    delete newState[objKey];
                    return newState;
                  });
                }
              }}
              title={`Key name for ${objKey}`}
            />
            <input
              type="number"
              className="w-24 md:w-32 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:border-blue-300"
              value={objValue}
              onChange={(e) => handleObjectValueChange(key, objKey, Number(e.target.value))}
              title={`Value for ${objKey}`}
            />
            <button
              type="button"
              onClick={() => handleDeleteObjectKey(key, objKey)}
              className="h-9 w-9 flex items-center justify-center text-gray-500 hover:text-red-500 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              ×
            </button>
          </div>
        ))}
        
        <div className="flex gap-2 items-center mt-3">
          <input
            type="text"
            placeholder="New key"
            value={newObjectKey}
            onChange={(e) => setNewObjectKey(e.target.value)}
            className="flex-1 h-9 text-sm px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="number"
            placeholder="Value"
            value={newObjectValue}
            onChange={(e) => setNewObjectValue(e.target.value)}
            className="w-24 md:w-32 h-9 text-sm px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="button"
            onClick={() => handleAddObjectKeyValue(key)}
            className="h-9 w-9 flex items-center justify-center text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            +
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {selectedActivity && (
        <div className="space-y-3 md:space-y-4">
          {Object.entries(selectedActivity).map(([key, value]) => {
            
            const paramValue = parameters[key] !== undefined ? parameters[key] : value;
            return (
              <div key={key} className="mb-3 md:mb-4">
                <label className="block text-gray-700 font-medium mb-1 md:mb-2 text-sm md:text-base">
                  {formatString(key, 'Title Case')}
                </label>
                {Array.isArray(value) && !Array.isArray(value[0]) ? (
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex flex-col md:flex-row gap-2 md:gap-3">
                      <div className="flex-1">
                        <Select
                          isMulti
                          options={getAvailableOptions(key, paramValue as string[])}
                          value={(paramValue as string[]).map(item => ({ value: item, label: item }))}
                          onChange={(selectedOptions) => {
                            const newValues = (selectedOptions as SelectOption[]).map((opt) => opt.value);
                            handleParameterChange(key, newValues);
                          }}
                          className="w-full"
                          placeholder="Select options"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '38px',
                              '@media (min-width: 768px)': {
                                minHeight: '42px',
                              },
                            }),
                            multiValue: (base) => ({
                              ...base,
                              fontSize: '0.85rem',
                              '@media (min-width: 768px)': {
                                fontSize: '0.9rem',
                              },
                            }),
                          }}
                        />
                      </div>
                      {isStringArray(paramValue) && (
                        <div className="flex gap-1 mt-2 md:mt-0">
                          <input
                            type="text"
                            placeholder="Add new value"
                            value={newValue}
                            onChange={handleInputChange}
                            onKeyDown={(e) => handleKeyDown(e, key)}
                            className="h-9 md:h-10 flex-1 md:flex-none md:w-[150px] lg:w-[180px] text-sm md:text-base px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => handleAddNewValue(key, newValue)}
                            className="h-9 md:h-10 w-9 md:w-10 flex items-center justify-center text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : isObjectParameter(key, value) ? (
                  renderObjectParameter(key, paramValue as Record<string, number>)
                ) : typeof value === "number" ? (
                  typeof paramValue === "number" || typeof paramValue === "string" ? (
                    <input
                      type="number"
                      className="w-full border rounded px-3 py-2 md:py-2.5 text-sm md:text-base focus:outline-none focus:ring focus:border-blue-300"
                      value={paramValue}
                      onChange={(e) => handleParameterChange(key, Number(e.target.value))}
                      title={`Input for ${key}`}
                      placeholder={`Enter ${key}`}
                    />
                  ) : (
                    <div className="w-full border rounded px-3 py-2 md:py-2.5 text-sm md:text-base bg-gray-50">
                      {formatParameterValue(paramValue as ParameterValue)}
                    </div>
                  )
                ) : (
                  <div className="w-full border rounded px-3 py-2 md:py-2.5 text-sm md:text-base bg-gray-50">
                    {formatParameterValue(paramValue as ParameterValue)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}